// ========================================
// OUTLOOK OAUTH CALLBACK HANDLER
// ========================================
import React, { useEffect, useState } from 'react';
import { auth } from '../../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

const OutlookCallback: React.FC = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Outlook Calendar koppeling aan het verwerken...');
  
  useEffect(() => {
    // Use sessionStorage to prevent double execution across component remounts
    const executionKey = 'outlook_callback_executing';
    const isExecuting = sessionStorage.getItem(executionKey);
    
    console.log('🔄 OutlookCallback useEffect triggered');
    console.log('🔍 Checking execution status:', isExecuting);
    
    // Prevent double execution due to React Strict Mode
    if (isExecuting === 'true') {
      console.log('⏭️ Callback already executing, skipping...');
      return;
    }
    
    console.log('🚀 OutlookCallback component mounted');
    console.log('📍 Current URL:', window.location.href);
    console.log('⚡ Marking callback as executing');
    
    // Mark as executing BEFORE calling handleCallback
    sessionStorage.setItem(executionKey, 'true');
    
    console.log('🎯 About to call handleCallback()');
    handleCallback();
    
    // Cleanup function to remove flag when component unmounts (optional)
    return () => {
      console.log('🧹 Component unmounting, keeping execution flag');
      // DON'T remove the flag here, as we want to prevent re-execution
      // The flag will be cleared on the next page load naturally
    };
  }, []); // Empty deps - only run once per page load
  
  const handleCallback = async () => {
    try {
      // Get code and state from URL params
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const error = params.get('error');
      const errorDescription = params.get('error_description');
      
      // Check for errors from Microsoft
      if (error) {
        setStatus('error');
        setMessage(errorDescription || 'OAuth autorisatie geweigerd');
        
        const returnUrl = sessionStorage.getItem('outlookCalendarReturnUrl') || '#agenda';
        sessionStorage.removeItem('outlookCalendarReturnUrl');
        
        setTimeout(() => {
          window.location.href = window.location.origin + returnUrl;
        }, 3000);
        return;
      }
      
      // Validate required params
      if (!code) {
        setStatus('error');
        setMessage('Ongeldige callback parameters');
        
        const returnUrl = sessionStorage.getItem('outlookCalendarReturnUrl') || '#agenda';
        sessionStorage.removeItem('outlookCalendarReturnUrl');
        
        setTimeout(() => {
          window.location.href = window.location.origin + returnUrl;
        }, 3000);
        return;
      }
      
      console.log('🔐 Processing Outlook OAuth callback...');
      
      // ⚠️ CRITICAL: Wait for Firebase auth to be ready
      setMessage('Wachten op authenticatie...');
      console.log('⏳ Waiting for Firebase auth to be ready...');
      
      // Wait for Firebase auth state (max 10 seconds)
      const waitForAuth = new Promise<boolean>((resolve) => {
        let attempts = 0;
        const maxAttempts = 20; // 10 seconds (20 * 500ms)
        
        const checkAuth = () => {
          attempts++;
          console.log(`🔍 Auth check attempt ${attempts}/${maxAttempts}, currentUser:`, auth.currentUser?.email || 'null');
          
          if (auth.currentUser) {
            console.log('✅ Firebase auth ready, user:', auth.currentUser.email);
            resolve(true);
          } else if (attempts >= maxAttempts) {
            console.log('⏱️ Auth timeout - user not logged in');
            resolve(false);
          } else {
            setTimeout(checkAuth, 500);
          }
        };
        
        checkAuth();
      });
      
      const isAuthenticated = await waitForAuth;
      
      if (!isAuthenticated) {
        setStatus('error');
        setMessage('Je moet ingelogd zijn om Outlook Calendar te koppelen. Log eerst in en probeer opnieuw.');
        setTimeout(() => {
          window.location.href = window.location.origin;
        }, 4000);
        return;
      }
      
      setMessage('Outlook Calendar token aan het ophalen...');
      
      // Get redirect URI
      const redirectUri = process.env.REACT_APP_OUTLOOK_REDIRECT_URI || 
        (process.env.NODE_ENV === 'production' 
          ? 'https://www.superkonnected.com/outlook-callback'
          : 'http://localhost:3001/outlook-callback');
      
      console.log('🔗 Using redirect URI:', redirectUri);
      
      // Call Firebase Cloud Function to exchange code for tokens
      const functions = getFunctions(undefined, 'europe-west1');
      const linkOutlookAccount = httpsCallable(functions, 'linkOutlookAccount');
      
      console.log('📞 Calling linkOutlookAccount Firebase Function...');
      const result = await linkOutlookAccount({
        code: code,
        redirectUri: redirectUri
      });
      
      const data = result.data as any;
      
      // Clear execution flag after callback completes (success or fail)
      sessionStorage.removeItem('outlook_callback_executing');
      console.log('🧹 Cleared execution flag');
      
      if (data.success || data.connected) {
        setStatus('success');
        setMessage('✅ Outlook Calendar succesvol gekoppeld!');
        console.log('✅ Outlook Calendar OAuth successful!');
        
        // Get the return URL from sessionStorage (set before OAuth flow)
        const returnUrl = sessionStorage.getItem('outlookCalendarReturnUrl') || '#agenda';
        sessionStorage.removeItem('outlookCalendarReturnUrl');
        
        // Trigger custom event for UI update
        window.dispatchEvent(new CustomEvent('outlookCalendarConnected', { detail: data }));
        
        // Redirect back to where user came from after 2 seconds
        setTimeout(() => {
          window.location.href = window.location.origin + returnUrl + '?outlookSuccess=true';
        }, 2000);
      } else {
        setStatus('error');
        setMessage(data.error || data.message || 'Koppeling mislukt');
        
        const returnUrl = sessionStorage.getItem('outlookCalendarReturnUrl') || '#agenda';
        sessionStorage.removeItem('outlookCalendarReturnUrl');
        
        setTimeout(() => {
          window.location.href = window.location.origin + returnUrl + '?outlookError=' + encodeURIComponent(data.error || 'Unknown error');
        }, 3000);
      }
      
    } catch (error: any) {
      console.error('❌ OAuth callback error:', error);
      
      // Clear execution flag on error
      sessionStorage.removeItem('outlook_callback_executing');
      console.log('🧹 Cleared execution flag (error)');
      
      setStatus('error');
      setMessage(error.message || 'Er is een fout opgetreden');
      
      const returnUrl = sessionStorage.getItem('outlookCalendarReturnUrl') || '#agenda';
      sessionStorage.removeItem('outlookCalendarReturnUrl');
      
      setTimeout(() => {
        window.location.href = window.location.origin + returnUrl + '?outlookError=' + encodeURIComponent(error.message || 'Unknown error');
      }, 3000);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        {/* Status Icon */}
        <div className="text-center mb-6">
          {status === 'processing' && (
            <div className="inline-block">
              <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-blue-600 mx-auto"></div>
              <div className="text-6xl mt-4">📅</div>
            </div>
          )}
          
          {status === 'success' && (
            <div className="animate-bounce">
              <div className="text-8xl">✅</div>
            </div>
          )}
          
          {status === 'error' && (
            <div className="animate-pulse">
              <div className="text-8xl">❌</div>
            </div>
          )}
        </div>
        
        {/* Status Message */}
        <h2 className={`text-2xl font-bold text-center mb-4 ${
          status === 'success' ? 'text-green-600' : 
          status === 'error' ? 'text-red-600' : 
          'text-gray-800'
        }`}>
          {status === 'processing' && 'Outlook Calendar Koppelen...'}
          {status === 'success' && 'Gekoppeld!'}
          {status === 'error' && 'Oeps!'}
        </h2>
        
        <p className="text-center text-gray-600 mb-6">
          {message}
        </p>
        
        {/* Progress Steps */}
        {status === 'processing' && (
          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold mr-3">
                ✓
              </div>
              <span>Autorisatie ontvangen</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center mr-3">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              </div>
              <span>Access token ophalen...</span>
            </div>
            <div className="flex items-center text-sm text-gray-400">
              <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-xs mr-3">
                3
              </div>
              <span>Profiel ophalen</span>
            </div>
            <div className="flex items-center text-sm text-gray-400">
              <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-xs mr-3">
                4
              </div>
              <span>Verbinding opslaan</span>
            </div>
          </div>
        )}
        
        {/* Success Message */}
        {status === 'success' && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
            <p className="text-green-800 font-semibold mb-2">
              🎉 Je Outlook Calendar is nu gekoppeld!
            </p>
            <p className="text-sm text-green-700">
              Je agenda wordt nu gesynchroniseerd.
            </p>
          </div>
        )}
        
        {/* Error Message */}
        {status === 'error' && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <p className="text-red-800 font-semibold mb-2 text-center">
              Er ging iets mis bij het koppelen
            </p>
            <p className="text-sm text-red-700 text-center mb-4">
              Probeer het opnieuw of neem contact op met support.
            </p>
            <button
              onClick={() => window.location.href = window.location.origin + '#agenda'}
              className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Terug naar Agenda
            </button>
          </div>
        )}
        
        {/* Redirect Notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Je wordt automatisch doorgestuurd...
          </p>
        </div>
      </div>
    </div>
  );
};

export default OutlookCallback;
